import { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { AccountForm } from '@/components/account/AccountForm';
import { createAccount, getProducts } from '@/services/api';
import { trackAccount } from '@/lib/rum';
import type { AccountRequest, AccountUpdateRequest } from '@/types/account';
import type { ApiError } from '@/types/api';
import type { ProductDomain } from '@/types/product';

interface CreateAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateAccountModal: React.FC<CreateAccountModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { instance } = useMsal();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [productDomains, setProductDomains] = useState<ProductDomain[]>([]);

  useEffect(() => {
    if (isOpen) {
      getProducts(instance)
        .then(response => setProductDomains(response.domains))
        .catch(err => console.error('Failed to fetch products:', err));
    }
  }, [isOpen, instance]);

  const handleSubmit = async (data: AccountRequest | AccountUpdateRequest) => {
    setIsLoading(true);
    setError(null);
    trackAccount.createStarted();

    try {
      const accountData = data as AccountRequest;
      await createAccount(instance, accountData);
      trackAccount.createSuccess(accountData.name, accountData.productCode ?? 'unknown');
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to create account token:', err);
      trackAccount.createError(err);

      const apiError = err as ApiError;

      if (apiError.status === 409) {
        setError('An account token with this value already exists. Please use a different token.');
      } else if (apiError.status === 400) {
        setError(apiError.message || 'Invalid data. Please check your inputs and try again.');
      } else if (apiError.status === 401) {
        setError('Authentication failed. Please log in again.');
      } else {
        setError('Failed to create account token. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (!isLoading) {
      setError(null);
      onClose();
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !isLoading) {
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Account</DialogTitle>
          <DialogDescription>
            Create a new account for collecting feedback.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <AccountForm
          mode="create"
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={isLoading}
          productDomains={productDomains}
        />
      </DialogContent>
    </Dialog>
  );
};
